// @mui
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
// utils
import { fToNow } from 'src/utils/format-time';
// components
import Label from 'src/components/label';
import FileThumbnail from 'src/components/file-thumbnail';
import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

// Get icon based on notification type
const getNotificationIcon = (type) => {
  switch (type) {
    case 'task_assigned':
      return 'solar:checklist-minimalistic-bold-duotone';
    case 'subtask_assigned':
      return 'solar:clipboard-list-bold-duotone';
    case 'company_invitation':
      return 'solar:user-plus-bold-duotone';
    case 'invitation_accepted':
      return 'solar:check-circle-bold-duotone';
    case 'comment_mention':
      return 'solar:chat-round-dots-bold-duotone';
    case 'proposal_status_updated':
      return 'solar:document-text-bold-duotone';
    case 'proposal_shared':
      return 'solar:share-bold-duotone';
    case 'proposal_access_request':
      return 'solar:lock-keyhole-bold-duotone';
    case 'proposal_comment':
      return 'solar:chat-round-bold-duotone';
    case 'application_received':
      return 'solar:inbox-in-bold-duotone';
    case 'application_applied':
      return 'solar:document-add-bold-duotone';
    default:
      return 'solar:bell-bold-duotone';
  }
};

// Get icon color based on notification type
const getNotificationColor = (type) => {
  switch (type) {
    case 'task_assigned':
      return 'primary.main';
    case 'subtask_assigned':
      return 'info.main';
    case 'company_invitation':
      return 'warning.main';
    case 'invitation_accepted':
      return 'success.main';
    case 'comment_mention':
      return 'secondary.main';
    case 'proposal_status_updated':
      return 'warning.main';
    case 'proposal_shared':
      return 'primary.main';
    case 'proposal_access_request':
      return 'secondary.main';
    case 'proposal_comment':
      return 'info.main';
    case 'application_received':
      return 'success.main';
    case 'application_applied':
      return 'primary.main';
    default:
      return 'text.secondary';
  }
};

const getNotificationUrl = (notification) => {
  const { type, data } = notification;
  switch (type) {
    case 'task_assigned':
    case 'subtask_assigned':
    case 'comment_mention':
      if (data?.projectId) return `/dashboard/projects/${data.projectId}/kanban`;
      return null;
    case 'proposal_shared':
    case 'proposal_status_updated':
      if (data?.proposalId) return `/dashboard/proposals/list?proposalId=${data.proposalId}`;
      return null;
    case 'proposal_comment':
      if (data?.folderId) return `/dashboard/proposals/list?proposalId=${data.folderId}&sidebar=comments`;
      return null;
    case 'proposal_accepted':
    case 'proposal_rejected':
      // Resource-level accept/reject — payload carries the line-item id
      // and category so we can deep-link to the resource page.
      if (data?.category && data?.proposalItemId != null) {
        return `/dashboard/proposals/${data.category}/${data.proposalItemId}`;
      }
      if (data?.proposalId) return `/dashboard/proposals/list?proposalId=${data.proposalId}`;
      return null;
    case 'proposal_access_request':
      return `/dashboard/notifications/${notification.id}`;
    case 'application_received':
      return `/dashboard/application/received`;
    case 'application_applied':
    case 'job_application_submitted':
      return `/dashboard/application/applied`;
    case 'company_invitation':
      return null;
    case 'invitation_accepted':
      return null;
    default:
      return null;
  }
};

export default function NotificationItem({ notification, onMarkAsRead, onNavigate }) {
  const handleClick = () => {
    if (notification.isUnRead && onMarkAsRead) {
      onMarkAsRead();
    }
    const url = getNotificationUrl(notification);
    if (url && onNavigate) {
      onNavigate(url);
    }
  };

  const renderAvatar = (
    <ListItemAvatar>
      {notification.avatarUrl ? (
        <Avatar src={notification.avatarUrl} sx={{ bgcolor: 'background.neutral' }} />
      ) : (
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: 'background.neutral',
          }}
        >
          <Iconify
            icon={getNotificationIcon(notification.type)}
            width={24}
            sx={{ color: getNotificationColor(notification.type) }}
          />
        </Stack>
      )}
    </ListItemAvatar>
  );

  const renderText = (
    <ListItemText
      disableTypography
      primary={
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          {notification.title}
        </Typography>
      }
      secondary={
        <Stack spacing={0.5}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {notification.message}
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            sx={{ typography: 'caption', color: 'text.disabled' }}
            divider={
              <Box
                sx={{ width: 2, height: 2, bgcolor: 'currentColor', mx: 0.5, borderRadius: '50%' }}
              />
            }
          >
            {fToNow(notification.createdAt)}
            {notification.category}
          </Stack>
        </Stack>
      }
    />
  );

  const renderUnReadBadge = notification.isUnRead && (
    <Box
      sx={{
        top: 26,
        width: 8,
        height: 8,
        right: 20,
        borderRadius: '50%',
        bgcolor: 'info.main',
        position: 'absolute',
      }}
    />
  );

  const friendAction = (
    <Stack spacing={1} direction="row" sx={{ mt: 1.5 }}>
      <Button size="small" variant="contained">
        Accept
      </Button>
      <Button size="small" variant="outlined">
        Decline
      </Button>
    </Stack>
  );

  const projectAction = (
    <Stack alignItems="flex-start">
      <Box
        sx={{
          p: 1.5,
          my: 1.5,
          borderRadius: 1.5,
          color: 'text.secondary',
          bgcolor: 'background.neutral',
        }}
      >
        {reader(
          `<p><strong>@Jaydon Frankie</strong> feedback by asking questions or just leave a note of appreciation.</p>`
        )}
      </Box>

      <Button size="small" variant="contained">
        Reply
      </Button>
    </Stack>
  );

  const fileAction = (
    <Stack
      spacing={1}
      direction="row"
      sx={{
        pl: 1,
        p: 1.5,
        mt: 1.5,
        borderRadius: 1.5,
        bgcolor: 'background.neutral',
      }}
    >
      <FileThumbnail
        file="http://localhost:8080/httpsdesign-suriname-2015.mp3"
        sx={{ width: 40, height: 40 }}
      />

      <Stack spacing={1} direction={{ xs: 'column', sm: 'row' }} flexGrow={1} sx={{ minWidth: 0 }}>
        <ListItemText
          disableTypography
          primary={
            <Typography variant="subtitle2" component="div" sx={{ color: 'text.secondary' }} noWrap>
              design-suriname-2015.mp3
            </Typography>
          }
          secondary={
            <Stack
              direction="row"
              alignItems="center"
              sx={{ typography: 'caption', color: 'text.disabled' }}
              divider={
                <Box
                  sx={{
                    mx: 0.5,
                    width: 2,
                    height: 2,
                    borderRadius: '50%',
                    bgcolor: 'currentColor',
                  }}
                />
              }
            >
              <span>2.3 GB</span>
              <span>30 min ago</span>
            </Stack>
          }
        />

        <Button size="small" variant="outlined">
          Download
        </Button>
      </Stack>
    </Stack>
  );

  const tagsAction = (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 1.5 }}>
      <Label variant="outlined" color="info">
        Design
      </Label>
      <Label variant="outlined" color="warning">
        Dashboard
      </Label>
      <Label variant="outlined">Design system</Label>
    </Stack>
  );

  const paymentAction = (
    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
      <Button size="small" variant="contained">
        Pay
      </Button>
      <Button size="small" variant="outlined">
        Decline
      </Button>
    </Stack>
  );

  return (
    <ListItemButton
      disableRipple
      onClick={handleClick}
      sx={{
        p: 2.5,
        alignItems: 'flex-start',
        borderBottom: (theme) => `dashed 1px ${theme.palette.divider}`,
        ...(notification.isUnRead && {
          bgcolor: 'action.selected',
        }),
      }}
    >
      {renderUnReadBadge}

      {renderAvatar}

      <Stack sx={{ flexGrow: 1 }}>
        {renderText}
        {notification.type === 'friend' && friendAction}
        {notification.type === 'project' && projectAction}
        {notification.type === 'file' && fileAction}
        {notification.type === 'tags' && tagsAction}
        {notification.type === 'payment' && paymentAction}
      </Stack>
    </ListItemButton>
  );
}

NotificationItem.propTypes = {
  notification: PropTypes.object,
  onMarkAsRead: PropTypes.func,
  onNavigate: PropTypes.func,
};

// ----------------------------------------------------------------------

function reader(data) {
  return (
    <Box
      dangerouslySetInnerHTML={{ __html: data }}
      sx={{
        mb: 0.5,
        '& p': { typography: 'body2', m: 0 },
        '& a': { color: 'inherit', textDecoration: 'none' },
        '& strong': { typography: 'subtitle2' },
      }}
    />
  );
}
